/**
 * Jeff, Oct 1 — the `?invite=` token a Team Budget invite email links with.
 *
 * It is read once, when the wizard opens a session: the server binds the invite to
 * that session, and from then on the session holds it. So step navigation leaves it
 * off (`preserveRegistrationParams`), and these are the only places it is handled.
 */

import { REGISTRATION_INVITE_PARAM } from '@sportninja/common/features/registration/registrationUrlParams'

/** The invite token in a search string, or null. */
export const inviteFromSearch = (
  search: string | null | undefined
): string | null =>
  new URLSearchParams(search ?? '').get(REGISTRATION_INVITE_PARAM)?.trim() ||
  null

/**
 * `path` with the invite token put back on its query string — for the one path
 * rebuilt BEFORE a session exists to hold the invite: recovering from a stored
 * session that is gone. Without it the reader's fresh session would open without
 * the slot held for them.
 */
export const withInviteParam = (
  path: string,
  invite: string | null | undefined
): string => {
  if (!invite) {
    return path
  }
  const queryAt = path.indexOf('?')
  const base = queryAt === -1 ? path : path.slice(0, queryAt)
  const params = new URLSearchParams(
    queryAt === -1 ? '' : path.slice(queryAt + 1)
  )
  params.set(REGISTRATION_INVITE_PARAM, invite)

  return `${base}?${params.toString()}`
}

/** The search string without the invite token — once a session holds it, or it was declined. */
export const withoutInviteParam = (
  search: string | null | undefined
): string => {
  const params = new URLSearchParams(search ?? '')
  params.delete(REGISTRATION_INVITE_PARAM)
  const qs = params.toString()

  return qs ? `?${qs}` : ''
}
